'use client';

import { Box, Button as MUIButton, ButtonProps as MuiButtonProps, useTheme } from '@mui/material';
import { IconType, IconPosition } from '../../../types/Button';
import {
  Add,
  ArrowCircleRight,
  CheckCircle,
  CloudDownload,
  FileUpload,
  Info,
  Notifications,
} from '@mui/icons-material';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import { ReactNode } from 'react';
import { PulseLoader } from 'react-spinners';
import { Delete } from '@brainpool/icon-pack';
// Only include variant, size, and color
type ButtonBaseProps = Pick<
  MuiButtonProps,
  'variant' | 'size' | 'color' | 'sx' | 'type' | 'onClick' | 'disabled' | 'children'
>;

const iconMap = {
  [IconType.CheckCircle]: CheckCircle,
  [IconType.CloudDownload]: CloudDownload,
  [IconType.FileUpload]: FileUpload,
  [IconType.Forward]: ArrowCircleRight,
  [IconType.Info]: Info,
  [IconType.Notifications]: Notifications,
  [IconType.Plus]: Add,
  [IconType.AddCircle]: AddCircleIcon,
  [IconType.ArticleRoundedIcon]: ArticleRoundedIcon,
  [IconType.Email]: MailOutlineRoundedIcon,
  [IconType.Delete]: DeleteForeverRoundedIcon,
  [IconType.Share]: ShareRoundedIcon,
  [IconType.BrainpoolDelete]: Delete,
};

export interface ButtonProps extends ButtonBaseProps {
  label?: string;
  icon?: IconType;
  iconPosition?: IconPosition;
  children?: ReactNode;
  isLoading?: boolean;
  pulseLoaderSize?: number;
  onMouseEnter?: () => void;
}
export function Button({
  label,
  icon,
  iconPosition = IconPosition.Before,
  children,
  isLoading,
  pulseLoaderSize = 4,
  color = 'primary',
  onMouseEnter,
  ...rest
}: ButtonProps) {
  const theme = useTheme();
  const IconComponent = icon ? iconMap[icon] : null;
  const getColor = (color: string) => {
    switch (color) {
      case 'primary':
        return theme?.palette?.primary?.contrastText;
      case 'secondary':
        return theme?.palette?.secondary?.contrastText;
      case 'success':
        return theme?.palette?.success?.contrastText;
      case 'error':
        return theme?.palette?.error?.contrastText;
      default:
        return theme?.palette?.text?.primary;
    }
  };

  const pulseLoaderColor = getColor(color);

  if (isLoading) {
    return (
      <MUIButton color={color} {...rest}>
        {iconPosition === IconPosition.Before && (
          <Box sx={{ mr: label ? 1 : 0 }}>
            <PulseLoader size={pulseLoaderSize} color={pulseLoaderColor} />
          </Box>
        )}
        {children || label}
        {iconPosition === IconPosition.After && (
          <Box sx={{ ml: label ? 1 : 0 }}>
            <PulseLoader size={pulseLoaderSize} color={pulseLoaderColor} />
          </Box>
        )}
      </MUIButton>
    );
  }
  return (
    <MUIButton color={color} onMouseEnter={onMouseEnter} {...rest}>
      {iconPosition === IconPosition.Before && IconComponent && (
        <IconComponent sx={{ mr: label ? 1 : 0 }} />
      )}
      {children || label}
      {iconPosition === IconPosition.After && IconComponent && (
        <IconComponent sx={{ ml: label ? 1 : 0 }} />
      )}
    </MUIButton>
  );
}

export default Button;
